import {
  FULL_YEAR_DIGITS,
  LAST_HOUR_OF_DAY,
  LAST_MINUTE_OF_HOUR,
  LAST_SECOND_OF_MINUTE,
  MONTHS_PER_YEAR,
} from './date-picker.constants';
import { FieldValues } from './date-picker.model';

/**
 * The wire contract: a timezone-naive local date-time string, `2026-08-24T10:00:00`.
 *
 * No offset is emitted, and one present on a value read back in is ignored — the moment
 * a value names is resolved downstream, not here. A load stop, for instance, reads its
 * naive string in the stop's own timezone, which a browser offset would contradict.
 *
 * Empty is `null` in every mode. `undefined` is only ever *accepted* — a form is free to
 * spell emptiness that way — but never emitted: a filter that builds its request by
 * walking the form would otherwise keep a key whose value is `undefined`, which reads as
 * "no filter" to `JSON.stringify` and as "one active filter" to `Object.keys`.
 */

export const PickerMode = {
  Date: 'date',
  Time: 'time',
  DateTime: 'date-time',
  Range: 'range',
} as const;
export type PickerMode = (typeof PickerMode)[keyof typeof PickerMode];

export interface DateRangeValue {
  from: string | null;
  to: string | null;
}

export type DatePickerValue = string | DateRangeValue | null | undefined;

const pad = (value: number, width = 2): string => String(value).padStart(width, '0');

// TODO: Vit: Check if it's not an old requirement
/** Reproduces JS Date roll-over (e.g. Feb 31 -> Mar 3) exactly as the old code did. */
function toLocalDate(values: FieldValues, seconds: number): Date | null {
  if (values.year === null || values.month === null || values.day === null) return null;

  const date = new Date(
    values.year,
    values.month - 1,
    values.day,
    values.hour24 ?? 0,
    values.minute ?? 0,
    seconds,
    0,
  );
  // `new Date` reads a year below 100 as 19xx; setting it again keeps the year as typed.
  date.setFullYear(values.year, values.month - 1, values.day);

  return Number.isNaN(date.getTime()) ? null : date;
}

const formatDatePart = (date: Date): string =>
  `${pad(date.getFullYear(), FULL_YEAR_DIGITS)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const formatTimePart = (date: Date): string =>
  `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

/**
 * `seconds` mirrors the old `autocompleteTimeMode` behaviour: a "to" field commits at
 * :59 so an inclusive upper bound covers the whole minute.
 */
export function formatOutput(
  values: FieldValues,
  mode: Exclude<PickerMode, typeof PickerMode.Range>,
  seconds: 0 | typeof LAST_SECOND_OF_MINUTE = 0,
): string | null {
  if (mode === PickerMode.Time) {
    if (values.hour24 === null && values.minute === null) return null;
    return `${pad(values.hour24 ?? 0)}:${pad(values.minute ?? 0)}`;
  }

  const date = toLocalDate(values, mode === PickerMode.DateTime ? seconds : 0);
  if (!date) return null;

  if (mode === PickerMode.Date) return formatDatePart(date);

  return `${formatDatePart(date)}T${formatTimePart(date)}`;
}

/**
 * A range with neither end is `null`, not `{ from: null, to: null }` — one check on the
 * value instead of one per end. Half a range still travels as an object.
 */
export function formatRangeOutput(from: FieldValues, to: FieldValues): DateRangeValue | null {
  const range = {
    from: formatOutput(from, PickerMode.Date),
    to: formatOutput(to, PickerMode.Date),
  };

  return range.from || range.to ? range : null;
}

const OUTPUT_DATE_TIME =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/;
const OUTPUT_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const OUTPUT_TIME = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

const isTime = (hour: number, minute: number): boolean =>
  hour <= LAST_HOUR_OF_DAY && minute <= LAST_MINUTE_OF_HOUR;

/** Any year will do: `setFullYear` below replaces it, `new Date` only must not read it as 19xx. */
const PLACEHOLDER_YEAR = 2000;

/** A month and day the calendar actually has — a value never rolls over on the way in. */
function isCalendarDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > MONTHS_PER_YEAR || day < 1) return false;

  const lastDay = new Date(PLACEHOLDER_YEAR, month, 0);
  lastDay.setFullYear(year, month, 0);

  return day <= lastDay.getDate();
}

/** Reads a control value back into field values. Tolerant: never throws, null on failure. */
export function parseOutput(
  text: unknown,
  mode: Exclude<PickerMode, typeof PickerMode.Range>,
): Partial<FieldValues> | null {
  if (typeof text !== 'string' || !text.trim()) return null;
  const value = text.trim();

  if (mode === PickerMode.Time) {
    const match = OUTPUT_TIME.exec(value);
    if (!match || !isTime(Number(match[1]), Number(match[2]))) return null;
    return { hour24: Number(match[1]), minute: Number(match[2]) };
  }

  const dateOnly = OUTPUT_DATE.exec(value);
  if (dateOnly) {
    const [year, month, day] = [Number(dateOnly[1]), Number(dateOnly[2]), Number(dateOnly[3])];
    return isCalendarDate(year, month, day) ? { year, month, day } : null;
  }

  const full = OUTPUT_DATE_TIME.exec(value);
  if (!full) return null;
  if (!isCalendarDate(Number(full[1]), Number(full[2]), Number(full[3]))) return null;
  if (!isTime(Number(full[4]), Number(full[5]))) return null;

  return {
    year: Number(full[1]),
    month: Number(full[2]),
    day: Number(full[3]),
    hour24: Number(full[4]),
    minute: Number(full[5]),
  };
}
